// src/pages/KeysPage.jsx

import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button } from '@mui/material';
import { supabase } from '../../lib/supabaseClient';  // adjust path as needed

export default function KeysPage() {
  // 1) Local state for the four Azure settings + any error
  const [clientId, setClientId]         = useState('');
  const [tenantId, setTenantId]         = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [redirectUri, setRedirectUri]   = useState('');
  const [error, setError]               = useState('');

  // 2) On mount, fetch existing settings
  useEffect(() => {
    (async () => {
      const {
        data: { session }
      } = await supabase.auth.getSession();
      if (!session) return;  // not logged in yet

      const token = session.access_token;
      try {
        const res = await fetch('/api/azure-settings', {
          method: 'GET',
          headers: { 
            'Authorization': `Bearer ${token}` 
          },
          credentials: 'include'   // include session cookie for Express
        });
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        if (data.clientId) {
          setClientId(data.clientId);
          setTenantId(data.tenantId);
          setRedirectUri(data.redirectUri);
          // we never GET the secret back
        }
      } catch (err) {
        console.error('Could not load Azure settings:', err);
        setError('Failed to load existing settings');
      }
    })();
  }, []);

  // 3) Save & then get the Microsoft login URL
  const saveAndConnect = async () => {
    setError('');
    const {
      data: { session }
    } = await supabase.auth.getSession();
    if (!session) {
      setError('You must be logged in to continue');
      return;
    }
    const token = session.access_token;

    try {
      // A) Save settings
      const res1 = await fetch('/api/azure-settings', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',  // ensure session cookie is set for next step
        body: JSON.stringify({
          clientId,
          tenantId,
          clientSecret,
          redirectUri
        })
      });
      if (!res1.ok) {
        throw new Error((await res1.json()).error || await res1.text());
      }

      // B) Fetch the MSAL login URL
      const res2 = await fetch('/api/azure-auth-url', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      const { authUrl, error: urlError } = await res2.json();
      if (!res2.ok || !authUrl) {
        throw new Error(urlError || 'Could not get auth URL');
      }

      // C) Redirect to Microsoft login
      window.location.href = authUrl;

    } catch (err) {
      console.error('Save & Connect failed:', err);
      setError(err.message || 'Unexpected error');
    }
  };

  return (
    <Box maxWidth={600} mx="auto" my={4}>
      <Typography variant="h5" gutterBottom>
        Azure OAuth Configuration
      </Typography>

      <TextField
        label="Azure Client ID"
        fullWidth margin="normal"
        value={clientId}
        onChange={(e) => setClientId(e.target.value)}
      />
      <TextField
        label="Azure Tenant ID"
        fullWidth margin="normal"
        value={tenantId}
        onChange={(e) => setTenantId(e.target.value)}
      />
      <TextField
        label="Azure Client Secret"
        type="password"
        fullWidth margin="normal"
        value={clientSecret}
        onChange={(e) => setClientSecret(e.target.value)}
      />
      <TextField
        label="Redirect URI"
        fullWidth margin="normal"
        helperText="Must match your Azure App registration"
        value={redirectUri}
        onChange={(e) => setRedirectUri(e.target.value)}
      />

      {error && (
        <Typography color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}

      <Button
        variant="contained"
        color="primary"
        sx={{ mt: 2 }}
        onClick={saveAndConnect}
      >
        SAVE & CONNECT MICROSOFT
      </Button>
    </Box>
  );
}
